<script setup>
import { computed, onMounted, ref } from "vue";
import {
    ExclamationIcon,
    XIcon,
    CheckCircleIcon,
    InformationCircleIcon,
    XCircleIcon,
} from "@heroicons/vue/solid";

const props = defineProps({
    error: {
        type: Object,
        default: () => {},
    },
});

const emit = defineEmits(["dismiss-error"]);
const displayAlert = ref(true);
let inActiveTimeout = null;

const toggleDisplay = computed(() => {
    if (displayAlert.value) {
        return "alert-active";
    } else {
        return "alert-inactive";
    }
});

//computed properties to style the toast depending on the type of error
const icon = computed(() => {
    if (!props.error?.type || props.error.type === "info") {
        return InformationCircleIcon;
    } else if (props.error.type === "success") {
        return CheckCircleIcon;
    } else if (props.error.type === "warning") {
        return ExclamationIcon;
    } else if (props.error.type === "error") {
        return XCircleIcon;
    }
});

const backgroundColor = computed(() => {
    if (!props.error?.type || props.error.type === "info") {
        return "bg-Blue-50 border-Blue-200";
    } else if (props.error.type === "success") {
        return "bg-Green-50 border-Green-300";
    } else if (props.error.type === "warning") {
        return "bg-Yellow-50 border-Yellow-600";
    } else if (props.error.type === "error") {
        return "bg-Red-100 border-Red-300";
    }
});

const iconColor = computed(() => {
    if (!props.error?.type || props.error.type === "info") {
        return "text-Blue-400";
    } else if (props.error.type === "success") {
        return "text-Green-500";
    } else if (props.error.type === "warning") {
        return "text-Yellow-600";
    } else if (props.error.type === "error") {
        return "text-Red-400";
    }
});

const bodyColor = computed(() => {
    if (!props.error?.type || props.error.type === "info") {
        return "text-Blue-700";
    } else if (props.error.type === "success") {
        return "text-Green-700";
    } else if (props.error.type === "warning") {
        return "text-Yellow-700";
    } else if (props.error.type === "error") {
        return "text-Red-700";
    }
});

const headingColor = computed(() => {
    if (!props.error?.type || props.error.type === "info") {
        return "text-Blue-800";
    } else if (props.error.type === "success") {
        return "text-Green-800";
    } else if (props.error.type === "warning") {
        return "text-Yellow-800";
    } else if (props.error.type === "error") {
        return "text-Red-800";
    }
});

//function to click and dismiss the alert - a seperate listener exists on parent to actually remove on click so this is purely visual

function dismissToast() {
    displayAlert.value = false;
    emit("dismiss-error", props.error.id);
    clearTimeout(inActiveTimeout);
}

//onMount function to dismiss the alert after 5 seconds

onMounted(() => {
    // resetTimeout(); // uncomment this line to enable auto-dismissal
});

//function to pause timeout when hovering over the alert

function pauseTimeout() {
    clearTimeout(inActiveTimeout);
}

//function to resume timeout when leaving hover - restarts at 3s

function resetTimeout() {
	return null; // comment this line to enable auto-dismissal
    inActiveTimeout = setTimeout(() => {
        dismissToast();
    }, props.error.timeout ?? 4000);
}
</script>

<template>
    <div :class="toggleDisplay" class="mb-1" v-if="error">
        <div
			data-testid="error-toast"
            :class="[backgroundColor]"
            @mouseenter="pauseTimeout"
            @mouseleave="resetTimeout"
            @click="dismissToast()"
            class="
                whitespace-pre-line
                scroll-hide
                max-h-[150px]
                overflow-auto
                rounded-md
                scroll-smooth
                p-4
                z-10
                mx-auto
                max-w-5xl
                border-b-4
            "
        >
            <div class="flex">
                <div class="">
                    <icon
                        :class="iconColor"
                        class="h-5 w-5"
                        aria-hidden="true"
                    />
                </div>
                <div class="flex w-[100%] justify-between">
                    <div class="ml-3 mr-2">
                        <h3 :class="headingColor" class="text-sm font-medium">
                            {{ error.title }}
                        </h3>
                        <div :class="bodyColor" class="mt-2 text-sm">
                            <p>{{ error.message }}</p>
                        </div>
                    </div>
                    <div class="">
                        <XIcon
                            :class="iconColor"
                            class="h-5 w-5 cursor-pointer"
                            aria-hidden="true"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
.alert-active {
    opacity: 1;
    transition: all 0.5s;
    max-height: 150px;
}

.alert-inactive {
    margin-top: 0;
    margin-bottom: 0;
    border-bottom: 0px;
    padding-top: 0;
    padding-bottom: 0;
    opacity: 0;
    max-height: 0px;
    transition: all 0.5s;
    overflow: hidden;
}

/* styling for scrollbar if needed */

::-webkit-scrollbar {
    width: 10px;
}

::-webkit-scrollbar {
    width: 5px;
}

::-webkit-scrollbar-track {
    background: rgba(2, 2, 2, 0.034);
}

::-webkit-scrollbar-thumb {
    background: rgba(2, 2, 2, 0.096);
    border-radius: 2px;
}
</style>
